import type { TourStep } from './tour-types';

/** How long a tour waits for its screen to finish loading before giving up for this visit. */
const READY_TIMEOUT_MS = 5000;
const POLL_MS = 200;

const hasSize = (element: Element): boolean => {
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};

/**
 * The element a step points at: the first one carrying its anchor that takes up space.
 * A screen can render the same control twice, one copy per breakpoint with the other
 * hidden, so the first match in the DOM is not necessarily the one on screen.
 */
export const findAnchor = (anchor: string): HTMLElement | null => {
  const matches = document.querySelectorAll<HTMLElement>(`[data-tour="${anchor}"]`);
  return Array.from(matches).find(hasSize) ?? null;
};

/** On the page and taking up space: not hidden, not collapsed, not in a closed drawer. */
export const isAnchorVisible = (anchor: string): boolean => findAnchor(anchor) !== null;

/**
 * Something modal is open that the tour does not belong to: a dialog, or the mobile
 * navigation drawer. A tour never starts on top of someone's work.
 */
const isModalOpen = (): boolean =>
  !!document.querySelector('[role="dialog"]:not([data-tour-bubble]), [role="alertdialog"]');

/**
 * The screen is showing its empty, first-run state instead of its content: the shared
 * `FirstRunState`, or a screen's own explainer marked the same way.
 */
const isFirstRunShowing = (): boolean => !!document.querySelector('[data-first-run]');

type WaitForAnchorsParams = {
  steps: readonly TourStep[];
  /**
   * Not ready while a first-run state is on screen. Set for a screen's own tour, which is
   * about the screen with content in it; not for the platform tour, which is about the
   * sidebar, or for a replay someone asked for.
   */
  holdForFirstRun: boolean;
  /** True while the screen's data is still arriving. The tour does not start until it is false. */
  isLoading: () => boolean;
  onReady: (visible: TourStep[]) => void;
};

/**
 * Calls back with the steps whose anchors are on screen, once the screen is ready and
 * settled. Calls back with nothing after the timeout. Returns a cancel function.
 *
 * Ready means no modal open, nothing loading, no first-run state when that is held for,
 * and at least one anchor on screen. Settled means the same anchors were on screen at
 * the previous look as well: a screen that draws its frame before its data, then swaps
 * to its first-run state, is caught in between rather than toured.
 */
export function waitForAnchors({ steps, holdForFirstRun, isLoading, onReady }: WaitForAnchorsParams): () => void {
  const startedAt = Date.now();
  let timer: ReturnType<typeof setTimeout> | null = null;
  let previous: string | null = null;

  const check = () => {
    const ready = !isModalOpen() && !isLoading() && !(holdForFirstRun && isFirstRunShowing());
    const visible = ready ? steps.filter((step) => isAnchorVisible(step.anchor)) : [];
    const current = visible.length ? visible.map((step) => step.anchor).join(' ') : null;

    if (current && current === previous) return onReady(visible);
    if (Date.now() - startedAt >= READY_TIMEOUT_MS) return onReady([]);
    previous = current;
    timer = setTimeout(check, POLL_MS);
  };

  // A first look after the current render has painted.
  timer = setTimeout(check, POLL_MS);
  return () => {
    if (timer) clearTimeout(timer);
  };
}
